import {
  Button,
  ButtonMenu,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectField,
  numericColumn,
  ScrollableContent,
  SelectField,
  simpleHeader,
  Tag,
  TextField,
  Tooltip,
  TreeSelectField,
  useGridTableApi,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createProductOfferingsUrl, createProductOfferingUrl } from "src/RouteUrls";
import { priceCell, SearchBox } from "src/components";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  type ProductOfferingCostCalculatorPage_ComputeResultFragment,
  type ProductOfferingCostCalculatorPage_LineItemFragment,
  type ProductOfferingCostCalculatorPage_ProductOfferingFragment,
  useProductOfferingCostCalculatorMetaQuery,
  useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery,
  useProductOfferingCostCalculatorPage_ComputeLazyQuery,
} from "src/routes/developments/product-offerings/ProductOfferingCostCalculatorPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { type ProductOfferingCostCalculatorParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";

export function ProductOfferingCostCalculatorPage() {
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingCostCalculatorParams>();
  const query = useProductOfferingCostCalculatorMetaQuery({ variables: { id: productOfferingId, rpavId } });
  const [previousVersionId, setPreviousVersionId] = usePreviousVersionIdParam();

  return queryResult(query, ({ productOffering }) => (
    <>
      <PageHeader
        title={`${productOffering.displayName} — Cost Calculator`}
        breadcrumb={[
          { label: "Product Offerings", href: createProductOfferingsUrl() },
          {
            label: productOffering.displayName,
            href: createProductOfferingUrl(
              productOffering.id,
              rpavId,
              productOffering.development?.id,
              productOffering.project?.id,
            ),
          },
        ]}
        right={
          <div css={Css.df.aic.gap2.$}>
            {productOffering.aggregateVersions.length > 1 && (
              <ReadyPlanVersionHeader
                readyPlan={productOffering}
                versionId={previousVersionId}
                label="Previous Version"
                onSelect={setPreviousVersionId}
                allowUnset={true}
              />
            )}
            <ReadyPlanVersionHeader
              readyPlan={productOffering}
              versionId={productOffering.version.id}
              aggregateCheckoutId={productOffering.aggregateCheckout?.id}
            />
          </div>
        }
      />
      <DataView productOffering={productOffering} previousVersionId={previousVersionId} />
    </>
  ));
}

type OptionGroup = ProductOfferingCostCalculatorPage_ProductOfferingFragment["optionGroups"][number];
type RpoOption = OptionGroup["options"][number];
type LineItemOption = ProductOfferingCostCalculatorPage_LineItemFragment["options"][number];
type OptionWithGroupOrder = { id: string; displayName: string; groupOrder: number };
type CurrentVersion = ProductOfferingCostCalculatorPage_ProductOfferingFragment["version"];
type AggregateVersion = ProductOfferingCostCalculatorPage_ProductOfferingFragment["aggregateVersions"][number];

type Totals = { totalCostInCents: number; previousTotalCostInCents?: number; differenceTotalCostInCents?: number };

type GroupRowData = {
  id: string;
  displayName: string;
  totalCostInCents: number;
  previousTotalCostInCents?: number;
  differenceTotalCostInCents?: number;
};

/** A tliv with context from its parent ProtoProjectItem, for grouping. */
type TlivEntry = Omit<ProductOfferingCostCalculatorPage_LineItemFragment, "totalCostInCents"> & {
  rowId: string;
  // Previous-only comparison rows intentionally leave this undefined so the Current column renders as blank.
  totalCostInCents?: number;
  costCodeId: string;
  costCodeName: string;
  tradePartnerId: string;
  tradePartnerName: string;
  /** The last option (by optionGroup.order), used for "Group By: Option". */
  lastOption?: OptionWithGroupOrder;
  previousTotalCostInCents?: number;
  differenceTotalCostInCents?: number;
};

type Row =
  | { kind: "header" }
  | { kind: "totals"; data: Totals }
  | { kind: "group"; data: GroupRowData }
  | { kind: "tliv"; data: TlivEntry };

type GroupBy = "costCode" | "tradePartner" | "option" | "none";
const groupByOptions: { id: GroupBy; name: string }[] = [
  { id: "costCode", name: "Cost Code" },
  { id: "tradePartner", name: "Trade Partner" },
  { id: "option", name: "Option" },
  { id: "none", name: "None" },
];

type DataViewProps = {
  productOffering: ProductOfferingCostCalculatorPage_ProductOfferingFragment;
  previousVersionId?: string;
};

function DataView(props: DataViewProps) {
  const { productOffering, previousVersionId } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();
  const api = useGridTableApi<Row>();
  const [search, setSearch] = useState<string | undefined>();
  const [groupBy, setGroupBy] = useState<GroupBy>("costCode");

  const currentVersion = productOffering.version;
  const previousVersion = useMemo(
    () => productOffering.aggregateVersions.find((version) => version.id === previousVersionId),
    [productOffering.aggregateVersions, previousVersionId],
  );

  // Categorize option groups into the three tiers
  const { designPackageGroups, structuralAndAddonOptions, personalizationGroups } = useMemo(
    () => categorizeOptionGroups(productOffering.optionGroups),
    [productOffering.optionGroups],
  );

  // Build a lookup to classify option IDs into their tier + group (for URL → state hydration)
  const optionClassification = useMemo(() => {
    const map = new Map<string, { groupId: string; tier: "design" | "structural" | "personalization" }>();
    for (const group of productOffering.optionGroups) {
      const isDesign = group.forDesignExterior || group.forDesignInterior;
      if (group.forDesignPackages && !isDesign) continue;
      for (const opt of group.options) {
        if (isDesign) {
          map.set(opt.id, { groupId: group.id, tier: "design" });
        } else if (opt.type.isStructuralV2 || opt.type.isAddOnV2) {
          map.set(opt.id, { groupId: group.id, tier: "structural" });
        } else {
          map.set(opt.id, { groupId: group.id, tier: "personalization" });
        }
      }
    }
    return map;
  }, [productOffering.optionGroups]);

  // Parse initial option IDs from the `?options=1,2,3` query param (only on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialOptionIds = useMemo(() => parseOptionsParam(location.search), []);

  // State: one selected RPO ID per design-package RPOG (single-select)
  // Hydrate from URL params if present, otherwise default to the first active option per group
  const [designSelections, setDesignSelections] = useState<Record<string, string | undefined>>(() => {
    const selections: Record<string, string | undefined> = {};
    for (const id of initialOptionIds) {
      const info = optionClassification.get(id);
      if (info?.tier === "design") selections[info.groupId] = id;
    }
    // Only auto-select package options on a fresh page load with no explicit URL selections.
    if (initialOptionIds.isEmpty) {
      for (const group of designPackageGroups) {
        if (!selections[group.id]) {
          const firstEligible = group.options.find(
            (option) => option.active && hasSatisfiedOptionPrerequisites(option, Object.values(selections).compact()),
          );
          const firstActive = firstEligible ?? group.options.find((o) => o.active);
          if (firstActive) selections[group.id] = firstActive.id;
        }
      }
    }
    return selections;
  });
  // State: selected RPO IDs for structural & add-ons (multi-select)
  const [structuralIds, setStructuralIds] = useState<string[]>(() =>
    initialOptionIds.filter((id) => optionClassification.get(id)?.tier === "structural"),
  );
  // State: selected RPO IDs for personalization (multi-select)
  const [personalizationIds, setPersonalizationIds] = useState<string[]>(() =>
    initialOptionIds.filter((id) => optionClassification.get(id)?.tier === "personalization"),
  );

  // Merge all currently selected RPO IDs from the editable form state.
  const selectedOptionIds = useMemo(() => {
    return [...Object.values(designSelections), ...structuralIds, ...personalizationIds].compact();
  }, [designSelections, structuralIds, personalizationIds]);
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>(initialOptionIds);
  const disabledOptionIds = useMemo(
    () => getDisabledOptionIds(productOffering.optionGroups, selectedOptionIds),
    [productOffering.optionGroups, selectedOptionIds],
  );

  // Sync selected options back to the `?options=` query param
  const syncOptionsToUrl = useCallback(
    (ids: string[]) => {
      const params = new URLSearchParams(location.search);
      if (ids.isEmpty) {
        params.delete("options");
      } else {
        // I.e. ["rpo:1", "rpo:2"] → "1-2"
        params.set("options", ids.map((id) => id.replace("rpo:", "")).join("-"));
      }
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    },
    [navigate, location.pathname, location.search],
  );

  useEffect(() => {
    syncOptionsToUrl(appliedOptionIds);
  }, [appliedOptionIds, syncOptionsToUrl]);

  /** Distributes a flat list of option IDs into the three selection state buckets. */
  const loadOptionIds = useCallback(
    (ids: string[]) => {
      const newDesign: Record<string, string | undefined> = {};
      const newStructural: string[] = [];
      const newPersonalization: string[] = [];
      for (const id of ids) {
        const info = optionClassification.get(id);
        if (!info) continue;
        if (info.tier === "design") newDesign[info.groupId] = id;
        else if (info.tier === "structural") newStructural.push(id);
        else newPersonalization.push(id);
      }
      setDesignSelections(newDesign);
      setStructuralIds(newStructural);
      setPersonalizationIds(newPersonalization);
    },
    [optionClassification],
  );

  const [runCurrentCompute, query] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({ fetchPolicy: "no-cache" });
  const [runPreviousCompute, previousQuery] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({
    fetchPolicy: "no-cache",
  });

  const applySelections = useCallback(
    async (optionIds: string[]) => {
      if (optionIds.isEmpty) {
        setAppliedOptionIds([]);
        return;
      }

      const { data } = await runCurrentCompute({
        variables: { readyPlanId: productOffering.id, readyPlanOptionIds: optionIds, versionId: currentVersion.id },
      });
      const resolvedOptionIds = data?.computeReadyPlanCost.readyPlanOptions.map((option) => option.id) ?? [];

      if (data) {
        loadOptionIds(resolvedOptionIds);
        setAppliedOptionIds(resolvedOptionIds);
      }
    },
    [currentVersion.id, loadOptionIds, productOffering.id, runCurrentCompute],
  );

  const didAutoApplyInitialOptions = useRef(false);

  useEffect(() => {
    if (didAutoApplyInitialOptions.current || initialOptionIds.isEmpty) return;
    didAutoApplyInitialOptions.current = true;
    void applySelections(initialOptionIds);
  }, [applySelections, initialOptionIds]);

  useEffect(() => {
    if (!previousVersionId || appliedOptionIds.isEmpty) return;
    void runPreviousCompute({
      variables: {
        readyPlanId: productOffering.id,
        readyPlanOptionIds: appliedOptionIds,
        versionId: previousVersionId,
      },
    });
  }, [appliedOptionIds, previousVersionId, productOffering.id, runPreviousCompute]);

  // Refetch when contingency markup changes so cost updates without other interaction
  useEffect(() => {
    if (appliedOptionIds.nonEmpty) {
      void applySelections(appliedOptionIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOffering.contingencyMarkupPercentage]);

  const skip = appliedOptionIds.isEmpty;
  const currentComputeReadyPlanCost = skip
    ? undefined
    : (query.data?.computeReadyPlanCost ?? query.previousData?.computeReadyPlanCost);
  const resetSelections = useCallback(() => {
    loadOptionIds([]);
    setAppliedOptionIds([]);
  }, [loadOptionIds]);

  const csvPrefixRows: string[][] = [
    ["Plan", productOffering.displayName],
    // TODO: add computeProgramData back once the query field is uncommented
    // ["Sellable Sqft", String(query.data?.computeProgramData?.sellableSqft ?? "N/A")],
    ...(previousVersion
      ? [
          ["Previous Version", getAggregateVersionLabel(previousVersion)],
          ["Current Version", currentVersion.displayVersion],
        ]
      : [["Version", currentVersion.displayVersion]]),
  ];
  const csvFileName =
    [
      productOffering.displayName.replaceAll(".", "_"),
      ...(previousVersion
        ? [getAggregateVersionLabel(previousVersion), currentVersion.displayVersion]
        : [currentVersion.displayVersion]),
    ].join("_") + ".csv";

  return (
    <>
      <OptionSelectors
        designPackageGroups={designPackageGroups}
        designSelections={designSelections}
        onDesignChange={(groupId, value) => setDesignSelections((prev) => ({ ...prev, [groupId]: value }))}
        structuralAndAddonOptions={structuralAndAddonOptions}
        structuralIds={structuralIds}
        onStructuralChange={setStructuralIds}
        personalizationGroups={personalizationGroups}
        personalizationIds={personalizationIds}
        onPersonalizationChange={setPersonalizationIds}
        selectedOptionIds={selectedOptionIds}
        disabledOptionIds={disabledOptionIds}
        onReset={resetSelections}
        onApply={() => void applySelections(selectedOptionIds)}
        applying={query.loading || previousQuery.loading}
        configurationErrors={currentComputeReadyPlanCost?.configurationErrors}
      />
      <div css={Css.df.jcsb.aic.my1.$}>
        <div css={Css.df.aic.gap2.$}>
          <SearchBox onSearch={setSearch} />
          <SelectField
            label="Group By"
            value={groupBy}
            options={groupByOptions}
            onSelect={(v) => setGroupBy(v ?? "costCode")}
            sizeToContent
            labelStyle="inline"
            compact
          />
        </div>
        <ButtonMenu
          trigger={{ icon: "verticalDots" }}
          placement="right"
          items={[
            { label: "Download CSV", onClick: () => api.downloadToCsv(csvFileName) },
            {
              label: "Load Checkout",
              onClick: () => openModal({ content: <LoadCheckoutModal loadOptionIds={loadOptionIds} /> }),
            },
          ]}
        />
      </div>
      {!skip &&
        queryResult(query, ({ computeReadyPlanCost }) =>
          previousVersionId ? (
            queryResult(previousQuery, ({ computeReadyPlanCost: previousComputeReadyPlanCost }) => (
              <CostView
                api={api}
                currentResult={computeReadyPlanCost}
                previousResult={previousComputeReadyPlanCost}
                currentVersion={currentVersion}
                previousVersion={previousVersion}
                search={search}
                csvPrefixRows={csvPrefixRows}
                groupBy={groupBy}
              />
            ))
          ) : (
            <CostView
              api={api}
              currentResult={computeReadyPlanCost}
              currentVersion={currentVersion}
              search={search}
              csvPrefixRows={csvPrefixRows}
              groupBy={groupBy}
            />
          ),
        )}
    </>
  );
}

type OptionSelectorsProps = {
  designPackageGroups: OptionGroup[];
  designSelections: Record<string, string | undefined>;
  onDesignChange: (groupId: string, value: string | undefined) => void;
  structuralAndAddonOptions: RpoOption[];
  structuralIds: string[];
  onStructuralChange: (ids: string[]) => void;
  personalizationGroups: { id: string; name: string; children: { id: string; name: string }[] }[];
  personalizationIds: string[];
  onPersonalizationChange: (ids: string[]) => void;
  selectedOptionIds: string[];
  disabledOptionIds: string[];
  onReset: () => void;
  onApply: () => void;
  applying: boolean;
  configurationErrors?: string[];
};

/** Renders the option selection UI in two rows. */
function OptionSelectors(props: OptionSelectorsProps) {
  const {
    designPackageGroups,
    designSelections,
    onDesignChange,
    structuralAndAddonOptions,
    structuralIds,
    onStructuralChange,
    personalizationIds,
    onPersonalizationChange,
    personalizationGroups,
    selectedOptionIds,
    disabledOptionIds,
    onReset,
    onApply,
    applying,
    configurationErrors,
  } = props;

  return (
    <div css={Css.df.fdc.gap1.$}>
      {/* Row 1: Design package RPOGs — one single-select per group */}
      <div css={Css.df.gap2.flexWrap("wrap").$}>
        {designPackageGroups.map((group) => (
          <SelectField
            key={group.id}
            label={group.name}
            value={designSelections[group.id]}
            onSelect={(value) => onDesignChange(group.id, value)}
            options={group.options.filter((o) => o.active).sortBy((o) => o.displayName)}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.displayName}
            nothingSelectedText="—"
            disabledOptions={disabledOptionIds}
            sizeToContent
          />
        ))}
      </div>
      {/* Row 2: Structural & Add-Ons + Personalization multi-selects + apply action */}
      <div css={Css.df.gap2.flexWrap("wrap").$}>
        {structuralAndAddonOptions.nonEmpty && (
          <MultiSelectField
            label="Structural & Add-Ons"
            values={structuralIds}
            onSelect={onStructuralChange}
            options={structuralAndAddonOptions}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.displayName}
            nothingSelectedText="—"
            disabledOptions={disabledOptionIds}
            sizeToContent
          />
        )}
        {personalizationGroups.nonEmpty && (
          <TreeSelectField
            label="Personalization"
            values={personalizationIds}
            onSelect={(response) => onPersonalizationChange(response.leaf.values)}
            options={personalizationGroups}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
            groupOptions={personalizationGroups.map((g) => g.id)}
            nothingSelectedText="—"
            sizeToContent
            defaultCollapsed
            disabledOptions={disabledOptionIds}
          />
        )}
        <div css={Css.df.aic.mt1.$}>
          <div css={Css.df.aic.gap1.$}>
            <Button
              label={applying ? "Applying..." : "Apply"}
              disabled={selectedOptionIds.isEmpty || applying}
              onClick={onApply}
            />
            <Button variant="text" label="Reset" disabled={selectedOptionIds.isEmpty} onClick={onReset} />
          </div>
        </div>
        {/* Configuration errors from invalid option combinations */}
        {configurationErrors && configurationErrors.nonEmpty && (
          <div css={Css.df.aic.gap1.mt1.$}>
            <Tooltip
              title={
                <ul css={Css.pl2.$}>
                  {configurationErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              }
            >
              <Tag text={`${configurationErrors.length} Configuration Errors`} type="warning" />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

type CostViewProps = {
  api: GridTableApi<Row>;
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  previousResult?: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  currentVersion: CurrentVersion;
  previousVersion?: AggregateVersion;
  csvPrefixRows: string[][];
  search: string | undefined;
  groupBy: GroupBy;
};

/** Renders the cost breakdown GridTable. */
function CostView(props: CostViewProps) {
  const { api, currentResult, previousResult, currentVersion, previousVersion, search, csvPrefixRows, groupBy } = props;
  const columns = useMemo(() => createColumns(currentVersion, previousVersion), [currentVersion, previousVersion]);
  const optionById = useMemo(() => createOptionById(currentResult, previousResult), [currentResult, previousResult]);
  const rows = useMemo(
    () =>
      previousResult
        ? createComparisonRows(currentResult, previousResult, groupBy, optionById)
        : createRows(currentResult, groupBy, optionById),
    [currentResult, groupBy, optionById, previousResult],
  );
  return (
    <ScrollableContent virtualized>
      <GridTable
        api={api}
        columns={columns}
        rows={rows}
        filter={search}
        csvPrefixRows={csvPrefixRows}
        sorting={{ on: "client" }}
        as="virtual"
      />
    </ScrollableContent>
  );
}

/** Creates the grid columns. */
function createColumns(currentVersion: CurrentVersion, previousVersion?: AggregateVersion): GridColumn<Row>[] {
  const groupColumn = column<Row>({
    header: "Group",
    group: ({ displayName }) => displayName,
    tliv: emptyCell,
    totals: emptyCell,
    showIn: "csv",
  });
  const nameColumn = column<Row>({
    header: "Name",
    group: ({ id, displayName }) => ({
      content: () => (
        <Tooltip title={id} placement="left">
          {displayName}
        </Tooltip>
      ),
      value: displayName,
    }),
    tliv: (li) => ({
      content: () => (
        <Tooltip title={li.id} placement="left">
          {li.displayName}
        </Tooltip>
      ),
      value: li.displayName,
    }),
    totals: emptyCell,
    w: "600px",
  });
  const optionsColumn = column<Row>({
    header: "Options",
    group: emptyCell,
    tliv: (li) => ({
      content: () => (
        <div css={Css.df.fdc.$}>
          {li.options.map((o) => (
            <div key={o.id}>{o.code}</div>
          ))}
        </div>
      ),
      value: li.options.map((o) => o.code).join(", "),
    }),
    totals: emptyCell,
    w: "250px",
  });
  const costSourceColumn = column<Row>({
    header: "Cost Source",
    group: emptyCell,
    tliv: (li) => {
      const revision = li.preferredBidContractLineItem?.revision;
      if (!revision) return emptyCell;
      return {
        content: () => <Link to={revision.blueprintUrl.path}>{revision.displayName}</Link>,
        value: revision.displayName,
      };
    },
    totals: emptyCell,
    w: "400px",
  });
  const comparisonMode = !!previousVersion;
  const costColumns = comparisonMode
    ? [
        numericColumn<Row>({
          header: `Previous ${getAggregateVersionLabel(previousVersion)}`,
          group: (data) => priceCell({ valueInCents: data.previousTotalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.previousTotalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.previousTotalCostInCents }),
          w: "150px",
        }),
        numericColumn<Row>({
          header: `Current ${currentVersion.displayVersion}`,
          group: (data) => priceCell({ valueInCents: data.totalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.totalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.totalCostInCents }),
          w: "150px",
        }),
        numericColumn<Row>({
          header: "Difference",
          group: (data) => priceCell({ valueInCents: data.differenceTotalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.differenceTotalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.differenceTotalCostInCents }),
          w: "150px",
        }),
      ]
    : [
        numericColumn<Row>({
          header: "Total Cost",
          group: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          tliv: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          totals: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          w: "120px",
        }),
      ];

  return [
    collapseColumn<Row>({ totals: emptyCell }),
    groupColumn,
    nameColumn,
    optionsColumn,
    costSourceColumn,
    ...costColumns,
  ];
}

/** Creates the grid rows for a single version, optionally grouped by cost code, trade partner, or option. */
function createRows(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionById: Map<string, OptionWithGroupOrder>,
): GridDataRow<Row>[] {
  const entries = createTlivEntries(currentResult, optionById);
  const totals: Totals = { totalCostInCents: currentResult.totalCostInCents };
  return buildRows(entries, totals, groupBy);
}

function createComparisonRows(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  previousResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionById: Map<string, OptionWithGroupOrder>,
): GridDataRow<Row>[] {
  const previousEntries = createTlivEntries(previousResult, optionById);
  const currentEntries = createTlivEntries(currentResult, optionById);
  const entries = mergeTlivEntries(previousEntries, currentEntries);
  const totals: Totals = {
    totalCostInCents: currentResult.totalCostInCents,
    previousTotalCostInCents: previousResult.totalCostInCents,
    differenceTotalCostInCents: currentResult.totalCostInCents - previousResult.totalCostInCents,
  };
  return buildRows(entries, totals, groupBy);
}

function buildRows(entries: TlivEntry[], totals: Totals, groupBy: GroupBy): GridDataRow<Row>[] {
  if (groupBy === "none") {
    return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...toTlivRows(entries)];
  }
  // Group entries by the selected dimension
  const grouped = entries.groupBy((entry) => entryGroupKey(entry, groupBy));
  const groupEntries = Object.entries(grouped);
  const groupRows: GridDataRow<Row>[] = groupEntries.map(([groupId, items]) => ({
    kind: "group" as const,
    id: groupId,
    pin: groupBy === "option" && groupId === "base" ? "first" : undefined,
    data: {
      id: groupId,
      displayName: entryGroupName(items[0], groupBy),
      totalCostInCents: items.map((entry) => entry.totalCostInCents).sum(),
      previousTotalCostInCents: items.map((entry) => entry.previousTotalCostInCents).sum(),
      differenceTotalCostInCents: items.map((entry) => entry.differenceTotalCostInCents).sum(),
    },
    children: toTlivRows(items),
  }));
  return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...groupRows];
}

/** Glue together curr/previous options to get options active in both. */
function createOptionById(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  previousResult?: ProductOfferingCostCalculatorPage_ComputeResultFragment,
): Map<string, OptionWithGroupOrder> {
  return [currentResult, previousResult]
    .compact()
    .flatMap((result) =>
      result.readyPlan.optionGroups.flatMap((rpog) =>
        rpog.options.map((rpo) => {
          return { ...rpo, groupOrder: rpog.order };
        }),
      ),
    )
    .uniqueBy((rpo) => rpo.id)
    .keyByObject((rpo) => rpo.id);
}

function createTlivEntries(
  result: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  optionById: Map<string, OptionWithGroupOrder>,
): TlivEntry[] {
  // Immediately flatMap proto project items → tliv entries with parent context
  const entries: TlivEntry[] = result.protoProjectItems.flatMap((pi, piIdx) => {
    const lineItems = [pi.itemTemplateItem, ...pi.modifyingTemplateItems].compact();
    const tp = pi.preferredPotentialBidContractLineItem?.bidContractLineItem?.revision.bidContract.tradePartner;
    /** Shared grouping dimensions from the parent ProtoProjectItem. */
    function dims(opts: readonly LineItemOption[]) {
      const lastOption = opts
        .map(
          (option) =>
            optionById.get(option.id) ??
              // Even if we union "previous version options" + "current version options" the TLIs themselves
              // can still be tagged with active=false options, and so lead to a missing option error
              { id: option.id, displayName: `Archived Option ${option.id}`, groupOrder: Number.MAX_SAFE_INTEGER },
        )
        .sortBy((option) => option.groupOrder).last;
      return {
        costCodeId: pi.item.costCode.id,
        costCodeName: pi.item.costCode.displayName,
        tradePartnerId: tp?.id ?? "unassigned",
        tradePartnerName: tp?.name ?? "Homebound Internal Estimate",
        lastOption,
      };
    }
    if (lineItems.nonEmpty) {
      return lineItems.map((li) => ({ ...li, rowId: li.id, ...dims(li.options) }) as TlivEntry);
    }
    // Synthetic entry for proto items with no backing line items (e.g. contingency)
    return [
      {
        id: "tli:0",
        rowId: `pi-${piIdx}-tli:0`,
        displayName: pi.name,
        options: [],
        preferredBidContractLineItem: undefined,
        totalCostInCents: pi.totalCostInCents,
        ...dims([]),
      } as TlivEntry,
    ];
  });
  return entries;
}

/** Converts tliv entries into GridDataRows. */
function toTlivRows(entries: TlivEntry[]): GridDataRow<Row>[] {
  return entries.map((e) => ({ kind: "tliv" as const, id: e.rowId, data: e }));
}

function mergeTlivEntries(previousEntries: TlivEntry[], currentEntries: TlivEntry[]): TlivEntry[] {
  const previousById = previousEntries.keyBy((entry) => entry.id);
  const currentById = currentEntries.keyBy((entry) => entry.id);
  const orderedIds = [...Object.keys(previousById), ...Object.keys(currentById)].unique();
  return orderedIds.map((id) => {
    const prevEntry = previousById[id];
    const currEntry = currentById[id];
    const baseEntry = currEntry ?? prevEntry ?? fail("Expected either a current or previous cost entry");
    const previousTotalCostInCents = prevEntry?.totalCostInCents;
    const currentTotalCostInCents = currEntry?.totalCostInCents;
    return {
      ...baseEntry,
      rowId: currEntry?.rowId ?? prevEntry?.rowId ?? baseEntry.rowId,
      totalCostInCents: currentTotalCostInCents,
      previousTotalCostInCents,
      differenceTotalCostInCents: (currentTotalCostInCents ?? 0) - (previousTotalCostInCents ?? 0),
    };
  });
}

/** Returns the group key for a tliv entry based on the active groupBy mode. */
function entryGroupKey(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeId;
  if (groupBy === "tradePartner") return entry.tradePartnerId;
  return entry.lastOption?.id ?? "base";
}

/** Returns the group display name for a tliv entry based on the active groupBy mode. */
function entryGroupName(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeName;
  if (groupBy === "tradePartner") return entry.tradePartnerName;
  return entry.lastOption?.displayName ?? "Base House";
}

/** Return the label for putting in the csv. */
function getAggregateVersionLabel(
  version: ProductOfferingCostCalculatorPage_ProductOfferingFragment["aggregateVersions"][number],
) {
  return version.status.code === AggregateVersionStatus.Draft
    ? `Draft of v${version.version ?? "?"}`
    : `v${version.version ?? "?"}`;
}

function usePreviousVersionIdParam() {
  const [{ prev }, setQueryParams] = useQueryParams({ prev: StringParam });
  const setPreviousVersionId = useCallback(
    (versionId: string | undefined) => {
      setQueryParams({ prev: versionId?.replace(":", "_") }, "replaceIn");
    },
    [setQueryParams],
  );
  return [prev?.replace("_", ":"), setPreviousVersionId] as const;
}

type LoadCheckoutModalProps = { loadOptionIds: (ids: string[]) => void };

/** Modal that loads selected options from a checkout config by ID. */
function LoadCheckoutModal(props: LoadCheckoutModalProps) {
  const { loadOptionIds } = props;
  const { closeModal } = useModal();
  const [configId, setConfigId] = useState<string | undefined>();
  const [fetchCheckout, { loading, error }] = useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery();

  async function handleLoad() {
    if (!configId) return;
    const { data } = await fetchCheckout({ variables: { id: configId } });
    if (data) {
      loadOptionIds(data.checkoutConfig.options.map((o) => o.id));
      closeModal();
    }
  }

  return (
    <>
      <ModalHeader>Load Checkout Config</ModalHeader>
      <ModalBody>
        <TextField label="Checkout Config ID" value={configId} onChange={setConfigId} placeholder="ccfg:1234" />
        {error && <div css={Css.sm.red600.mt1.$}>Failed to load: {error.message}</div>}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Button label="Load" disabled={!configId || loading} onClick={handleLoad} />
      </ModalFooter>
    </>
  );
}

function getDisabledOptionIds(optionGroups: OptionGroup[], selectedOptionIds: string[]): string[] {
  return optionGroups.flatMap((group) =>
    group.options
      .filter((option) => option.active && !hasSatisfiedOptionPrerequisites(option, selectedOptionIds))
      .map((option) => option.id),
  );
}

function hasSatisfiedOptionPrerequisites(option: RpoOption, selectedOptionIds: string[]): boolean {
  if (option.optionPrerequisites.isEmpty) return true;

  const selectedOptionIdsSet = new Set(selectedOptionIds);
  const prereqsByGroup = option.optionPrerequisites.groupBy((prereq) => prereq.optionGroup.id);
  return prereqsByGroup
    .toValues()
    .every((prereqsInGroup) => prereqsInGroup.some((prereq) => selectedOptionIdsSet.has(prereq.id)));
}

/** Categorizes option groups into design-package, structural/add-on, and personalization tiers. */
function categorizeOptionGroups(optionGroups: OptionGroup[]) {
  // Tier 1: Design package RPOGs — one single-select per group
  const designPackageGroups = optionGroups
    .filter((g) => g.forDesignExterior || g.forDesignInterior)
    .sortBy((g) => g.order);

  // Tier 2 & 3: Non-design-package RPOGs, split by type. Sorted by RPOG order, then by displayName within each group.
  const nonDesignGroups = optionGroups.filter((g) => !g.forDesignPackages).sortBy((g) => g.order);
  const allNonDesignOptions = nonDesignGroups.flatMap((g) =>
    g.options.filter((o) => o.active).sortBy((o) => o.displayName),
  );

  // I.e. "Add Bedroom", "Add Garage Bay", structural upgrades
  const structuralAndAddonOptions = allNonDesignOptions.filter((o) => o.type.isStructuralV2 || o.type.isAddOnV2);

  // I.e. personalization product/package options
  const personalizationOptions = allNonDesignOptions.filter((o) => !o.type.isStructuralV2 && !o.type.isAddOnV2);

  // Personalization groups as a tree for TreeSelectField (groups → options)
  const personalizationGroups = nonDesignGroups
    .filter((g) => g.options.some((o) => o.active && !o.type.isStructuralV2 && !o.type.isAddOnV2))
    .sortBy((g) => g.order)
    .map((g) => ({
      id: g.id,
      name: g.name,
      children: g.options
        .filter((o) => o.active && !o.type.isStructuralV2 && !o.type.isAddOnV2)
        .sortBy((o) => o.displayName)
        .map((o) => ({ id: o.id, name: o.displayName })),
    }));

  return { designPackageGroups, structuralAndAddonOptions, personalizationOptions, personalizationGroups };
}

/** Parses `?options=1,2,3` into `["rpo:1", "rpo:2", "rpo:3"]`. */
function parseOptionsParam(search: string): string[] {
  const raw = new URLSearchParams(search).get("options");
  if (!raw) return [];
  return raw.split("-").map((id) => `rpo:${id.trim()}`);
}
